
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

/**
 * Interface for comprehensive educational content analysis
 */
interface ContentAnalysis {
  topics: Topic[];
  modules: Module[];
  lessons: Lesson[];
  contentGaps: ContentGap[];
  statistics: ContentStatistics;
  recommendations: Recommendation[];
}

interface Topic {
  id: string;
  title: string;
  description: string;
  order_index: number;
  status: string;
  moduleCount: number;
  lessonCount: number;
  contentPercentage: number;
}

interface Module {
  id: string;
  title: string;
  description: string;
  topic_id: string;
  order_index: number;
  status: string;
  lessonCount: number;
  contentPercentage: number;
}

interface Lesson {
  id: string;
  title: string;
  description: string;
  module_id: string;
  order_index: number;
  status: string;
  hasContent: boolean;
  contentQuality: 'empty' | 'minimal' | 'partial' | 'complete';
  estimatedWords: number;
}

interface ContentGap {
  type: 'missing_lessons' | 'empty_content' | 'incomplete_module' | 'duplicate_topic';
  severity: 'low' | 'medium' | 'high' | 'critical';
  description: string;
  affectedIds: string[];
  recommendation: string;
}

interface ContentStatistics {
  totalTopics: number;
  totalModules: number;
  totalLessons: number;
  lessonsWithContent: number;
  contentCompletionRate: number;
  averageLessonsPerModule: number;
  averageModulesPerTopic: number;
  criticalGaps: number;
  duplicateContent: number;
}

interface Recommendation {
  priority: 'high' | 'medium' | 'low';
  category: 'content_creation' | 'structure_cleanup' | 'quality_improvement';
  title: string;
  description: string;
  estimatedEffort: string;
  impact: string;
}

/**
 * Hook to perform comprehensive analysis of educational content
 * Identifies gaps, duplicates, and provides actionable recommendations
 */
export const useEducationalAnalysis = () => {
  return useQuery({
    queryKey: ['educational-analysis'],
    queryFn: async (): Promise<ContentAnalysis> => {
      console.log("Starting comprehensive educational content analysis...");

      // Fetch all educational data in parallel
      const [topicsResult, modulesResult, lessonsResult] = await Promise.all([
        supabase.from('topics').select('*').eq('status', 'active').order('order_index'),
        supabase.from('modules').select('*').eq('status', 'active').order('order_index'),
        supabase.from('lessons').select('*').eq('status', 'active').order('order_index')
      ]);

      if (topicsResult.error) throw topicsResult.error;
      if (modulesResult.error) throw modulesResult.error;
      if (lessonsResult.error) throw lessonsResult.error;

      const rawTopics = topicsResult.data || [];
      const rawModules = modulesResult.data || [];
      const rawLessons = lessonsResult.data || [];

      console.log("Raw data fetched:", {
        topics: rawTopics.length,
        modules: rawModules.length,
        lessons: rawLessons.length
      });

      // Analyze lesson content quality
      const analyzedLessons: Lesson[] = rawLessons.map(lesson => {
        const hasContent = lesson.content && 
                          lesson.content !== '' && 
                          lesson.content !== '{}' &&
                          (typeof lesson.content === 'object' ? Object.keys(lesson.content).length > 0 : true);

        let contentQuality: 'empty' | 'minimal' | 'partial' | 'complete' = 'empty';
        let estimatedWords = 0;

        if (hasContent) {
          try {
            const contentObj = typeof lesson.content === 'string' 
              ? JSON.parse(lesson.content) 
              : lesson.content;
            
            const sections = contentObj?.content?.sections || [];
            const totalText = sections.map((s: any) => s.text || '').join(' ');
            estimatedWords = totalText.split(' ').filter((w: string) => w.length > 0).length;
            
            if (estimatedWords === 0) contentQuality = 'empty';
            else if (estimatedWords < 50) contentQuality = 'minimal';
            else if (estimatedWords < 200) contentQuality = 'partial';
            else contentQuality = 'complete';
          } catch (error) {
            console.warn(`Error parsing content for lesson ${lesson.id}:`, error);
            contentQuality = 'empty';
          }
        }

        return {
          id: lesson.id,
          title: lesson.title,
          description: lesson.description || '',
          module_id: lesson.module_id || '',
          order_index: lesson.order_index,
          status: lesson.status || 'active',
          hasContent,
          contentQuality,
          estimatedWords
        };
      });

      // Analyze modules with lesson statistics
      const analyzedModules: Module[] = rawModules.map(module => {
        const moduleLessons = analyzedLessons.filter(l => l.module_id === module.id);
        const lessonsWithContent = moduleLessons.filter(l => l.hasContent).length;
        const contentPercentage = moduleLessons.length > 0 
          ? Math.round((lessonsWithContent / moduleLessons.length) * 100) 
          : 0;

        return {
          id: module.id,
          title: module.title,
          description: module.description || '',
          topic_id: module.topic_id || '',
          order_index: module.order_index,
          status: module.status || 'active',
          lessonCount: moduleLessons.length,
          contentPercentage
        };
      });

      // Analyze topics with module and lesson statistics
      const analyzedTopics: Topic[] = rawTopics.map(topic => {
        const topicModules = analyzedModules.filter(m => m.topic_id === topic.id);
        const topicLessons = analyzedLessons.filter(l => 
          topicModules.some(m => m.id === l.module_id)
        );
        const lessonsWithContent = topicLessons.filter(l => l.hasContent).length;
        const contentPercentage = topicLessons.length > 0 
          ? Math.round((lessonsWithContent / topicLessons.length) * 100) 
          : 0;

        return {
          id: topic.id,
          title: topic.title,
          description: topic.description || '',
          order_index: topic.order_index,
          status: topic.status || 'active',
          moduleCount: topicModules.length,
          lessonCount: topicLessons.length,
          contentPercentage
        };
      });

      // Identify content gaps and issues
      const contentGaps: ContentGap[] = [];

      // Find topics with no modules
      analyzedTopics.filter(t => t.moduleCount === 0).forEach(topic => {
        contentGaps.push({
          type: 'incomplete_module',
          severity: 'high',
          description: `Topic "${topic.title}" has no modules`,
          affectedIds: [topic.id],
          recommendation: 'Create modules for this topic or consider removing it'
        });
      });

      // Find modules with no lessons
      analyzedModules.filter(m => m.lessonCount === 0).forEach(module => {
        contentGaps.push({
          type: 'missing_lessons',
          severity: 'high',
          description: `Module "${module.title}" has no lessons`,
          affectedIds: [module.id],
          recommendation: 'Add lessons to this module or consider removing it'
        });
      });

      // Find lessons with no content
      analyzedLessons.filter(l => !l.hasContent).forEach(lesson => {
        contentGaps.push({
          type: 'empty_content',
          severity: 'medium',
          description: `Lesson "${lesson.title}" has no content`,
          affectedIds: [lesson.id],
          recommendation: 'Add educational content to this lesson'
        });
      });

      // Find potential duplicate topics
      const titleGroups = analyzedTopics.reduce((acc, topic) => {
        const normalizedTitle = topic.title.toLowerCase().replace(/[^a-z\s]/g, '').trim();
        if (!acc[normalizedTitle]) acc[normalizedTitle] = [];
        acc[normalizedTitle].push(topic);
        return acc;
      }, {} as Record<string, Topic[]>);

      Object.values(titleGroups).filter(group => group.length > 1).forEach(duplicates => {
        contentGaps.push({
          type: 'duplicate_topic',
          severity: 'medium',
          description: `Potential duplicate topics: ${duplicates.map(t => t.title).join(', ')}`,
          affectedIds: duplicates.map(t => t.id),
          recommendation: 'Review and consolidate these similar topics'
        });
      });

      // Calculate comprehensive statistics
      const lessonsWithContent = analyzedLessons.filter(l => l.hasContent).length;
      const statistics: ContentStatistics = {
        totalTopics: analyzedTopics.length,
        totalModules: analyzedModules.length,
        totalLessons: analyzedLessons.length,
        lessonsWithContent,
        contentCompletionRate: analyzedLessons.length > 0 
          ? Math.round((lessonsWithContent / analyzedLessons.length) * 100) 
          : 0,
        averageLessonsPerModule: analyzedModules.length > 0 
          ? Math.round((analyzedLessons.length / analyzedModules.length) * 10) / 10 
          : 0,
        averageModulesPerTopic: analyzedTopics.length > 0 
          ? Math.round((analyzedModules.length / analyzedTopics.length) * 10) / 10 
          : 0,
        criticalGaps: contentGaps.filter(g => g.severity === 'critical' || g.severity === 'high').length,
        duplicateContent: contentGaps.filter(g => g.type === 'duplicate_topic').length
      };

      // Generate actionable recommendations
      const recommendations: Recommendation[] = [];

      // Priority content creation recommendations
      if (statistics.contentCompletionRate < 50) {
        recommendations.push({
          priority: 'high',
          category: 'content_creation',
          title: 'Complete Core Financial Topics',
          description: 'Focus on completing lessons for fundamental topics like Budgeting, Saving, and Banking Basics',
          estimatedEffort: '2-3 weeks',
          impact: 'Establishes solid foundation for learners'
        });
      }

      // Structure cleanup recommendations
      if (statistics.criticalGaps > 5) {
        recommendations.push({
          priority: 'high',
          category: 'structure_cleanup',
          title: 'Address Structural Issues',
          description: 'Remove empty modules, consolidate duplicate topics, and clean up organizational structure',
          estimatedEffort: '1 week',
          impact: 'Improves navigation and reduces confusion'
        });
      }

      // Quality improvement recommendations
      const minimalContent = analyzedLessons.filter(l => l.contentQuality === 'minimal').length;
      if (minimalContent > 10) {
        recommendations.push({
          priority: 'medium',
          category: 'quality_improvement',
          title: 'Enhance Lesson Content Quality',
          description: 'Expand brief lessons with more detailed explanations, examples, and interactive elements',
          estimatedEffort: '3-4 weeks',
          impact: 'Significantly improves learning experience'
        });
      }

      console.log("Analysis complete:", {
        contentGaps: contentGaps.length,
        statistics,
        recommendations: recommendations.length
      });

      return {
        topics: analyzedTopics,
        modules: analyzedModules,
        lessons: analyzedLessons,
        contentGaps,
        statistics,
        recommendations
      };
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};
